import { spLogTags } from '@eventr/common'
import {
    ColoursObj,
    MobileConfirmDialog,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { loadingSignal } from '../../../state/overlayState/overlaySignals'
import { groupChatName } from '../../../state/rocketchatState/rocketchatSignals'
import { EditMembersProvider } from '../../editItinerary/editMembers/editMembersProvider'
import { MembersList } from '../../editItinerary/editMembers/membersList'
import { NavigationView } from '../../layout/NavigationView'
import { createGroupChannel, useRocketChatContext } from '../rocketChatProvider'

export const CreateGroupChannel = () => {
    const { wpUser, users } = useBoundStore(
        useShallow((s) => ({
            wpUser: s.wpUser,
            users: s.users,
        }))
    )

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const { markAsRead } = useRocketChatContext()

    const currentUser = { type: 'User', option: wpUser?.id }

    const [selectedUsers, setSelectedUsers] = useState<any[]>([currentUser])

    const mappedUserIds: number[] = selectedUsers.map((u) => u.option) ?? []

    const onSave = async () => {
        try {
            loadingSignal.value = true

            await createGroupChannel({
                user_ids: mappedUserIds,
                channel_name_parts: [groupChatName?.value],
                _id: getRandomGroupId(),
            })
                .then(async (c) => {
                    if (c.status === 'success' && c.data) {
                        defaultAddSnack(
                            i18n.t('snack.rocket.createGroupSuccess'),
                            'success'
                        )
                        await bs().pullRocketChatChannels()
                        window.goBack()
                        return
                    } else if (c.status === 'error') {
                        const identifierObj = JSON.parse(c.data)

                        const error = createErrorMessage(
                            identifierObj.error_type
                        )

                        defaultAddSnack(error, 'error')
                        spLogTags('Error creating channel', [
                            'rocketchat',
                            'ERROR',
                        ])
                        spLogTags(c, ['rocketchat', 'ERROR'])
                    } else {
                        spLogTags(c, ['rocketchat', 'ERROR'])
                    }
                })
                .finally(() => {
                    loadingSignal.value = false
                })
        } catch (error) {
            loadingSignal.value = false
            defaultAddSnack(i18n.t('snack.rocket.createGroupError'), 'error')
            spLogTags('Exception creating channel', ['rocketchat', 'ERROR'])
            spLogTags(error, ['rocketchat', 'ERROR'])
        }
    }

    const unsavedChanges =
        selectedUsers.length > 1 || groupChatName?.value?.trim() !== ''

    return (
        <EditMembersProvider>
            <Stack
                height="100dvh"
                sx={{ backgroundColor: ColoursObj.backgrounds.primary }}
            >
                <NavigationView
                    navigationBarTitle={i18n.t('rocketChat.createGroup')}
                    backButtonAction={() => {
                        unsavedChanges
                            ? replaceDialog({
                                  node: (
                                      <MobileConfirmDialog
                                          header={i18n.t(
                                              'info-dialog.unsavedChangesTitle'
                                          )}
                                          text={i18n.t(
                                              'info-dialog.unsavedChangesBody'
                                          )}
                                          onCancel={() => {
                                              window.goBack()
                                              clearDialog()
                                          }}
                                          onConfirm={() => {
                                              clearDialog()
                                          }}
                                          cancelText={i18n.t('info-dialog.yes')}
                                          confirmText={i18n.t('info-dialog.no')}
                                      />
                                  ),
                                  backdropClosable: false,
                              })
                            : window.goBack()
                    }}
                    navigationBarTrailing={[
                        {
                            id: 'createButton',
                            buttonText: 'Create',
                            disabled:
                                selectedUsers.length >= 2 &&
                                (groupChatName?.value?.trim() || '') !== '' &&
                                groupChatName?.value?.length < 30
                                    ? false
                                    : true,
                            colour: ColoursObj.core.primary,
                            useButton: true,
                            action: async () => onSave(),
                        },
                    ]}
                    usePageTransition
                    userSearchBar
                    groupNameField
                >
                    <Stack
                        padding="10px 21px 50px"
                        display="flex"
                        flexDirection="column"
                    >
                        <MembersList
                            users={users}
                            wpUser={wpUser}
                            selectedUsers={selectedUsers ?? []}
                            setSelectedUsers={setSelectedUsers}
                            searchBar={false}
                            onRefresh={async () => {
                                await Promise.all([
                                    bs().getProfile(),
                                    bs().fetchUsers(),
                                    bs().forceItineraryMapUpdate(),
                                ])
                            }}
                        />
                    </Stack>
                </NavigationView>
            </Stack>
        </EditMembersProvider>
    )
}

function createErrorMessage(msg: string) {
    switch (msg) {
        case 'error-duplicate-channel-name':
            return i18n.t('snack.rocket.duplicateName')
        case 'error-invalid-room-name':
            return i18n.t('snack.rocket.invalidRoomName')
        case 'error-user-not-allowed':
            return i18n.t('snack.rocket.userNotAllowedCreate')
        case 'error-invalid-users':
            return i18n.t('snack.rocket.invalidUsers')
        case 'error-unauthorized':
            return i18n.t('snack.rocket.unauthorisedCreate')
        default:
            return i18n.t('snack.rocket.createGroupError')
    }
}

function getRandomGroupId() {
    return `group_${Date.now()}${Math.floor(Math.random() * 1000)}`
}
